import { useEffect } from "react";
import Layout from "../../components/blog/Layout";
import { getPostData, getSortedPostsData } from "../../lib/posts/abc-podatkow";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/pl";
import { serialize } from "next-mdx-remote/serialize";
import { MDXRemote } from "next-mdx-remote";
import Section from "../../components/Section";
import DeclarationBox from "../../components/DeclarationBox";
import DeclarationBoxItem from "../../components/DeclarationBoxItem";
import BackgroundSection from "../../components/BackgroundSection";
import SectionBox from "../../components/SectionBox";
import Author from "../../components/Author";
import Link from "next/link";
import BackButton from "../../components/BackButton";
import FAQ from "../../components/FAQ";
import { ArticleMetaProvider } from "../../components/ArticleMetaContext";
import {
	FloatVideo,
	ArticleVideo,
	DeclarationBoxIndividual,
	DeclarationModal,
} from "../../components/DynamicComponents";
import MDXComponents from "../../components/MDXComponents";
import ArticleStructuredData from "../../components/ArticleStructuredData";
import { resolveArticleAuthor } from "../../lib/authors";

dayjs.extend(localizedFormat);
dayjs.locale("pl");

const components = {
	...MDXComponents,
	FloatVideo,
	ArticleVideo,
	DeclarationBoxIndividual,
	BackgroundSection,
	SectionBox,
	Section,
	FAQ,
};

const CheckIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="12"
		height="12"
		viewBox="0 0 12 12"
		fill="none"
		aria-hidden="true"
		focusable="false"
	>
		<path
			d="M2.5 6.5L4.5 8.5L9.5 3.5"
			stroke="#138808"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const getDialogIdByTaxType = (blogPodatek) => {
	const dialogIds = {
		nieruchomosci: "podatek-od-nieruchomosci",
		transportowe: "podatek-od-srodkow-transportowych",
		rolny: "podatek-rolny",
		lesny: "podatek-lesny",
	};

	return dialogIds[blogPodatek] || "podatek-od-nieruchomosci";
};

const getAsideActionText = (postData) => {
	if (postData.blogActionText) {
		return postData.blogActionText;
	}

	if (postData.blogPodatek === "transportowe") {
		return "Przygotuj DT-1";
	}

	if (postData.blogPodatek === "nieruchomosci") {
		return "Przygotuj IN-1 lub DN-1";
	}

	if (postData.blogPodatek === "rolny") {
		return "Przygotuj deklarację rolną";
	}

	if (postData.blogPodatek === "lesny") {
		return "Przygotuj deklarację leśną";
	}

	return "Przygotuj deklarację";
};

const AsideStickyAction = ({ postData }) => {
	const actionText = getAsideActionText(postData);
	const isLinkAction = postData.blogWithAction === "link" || postData.dialog === "link";
	const isDialogAction =
		postData.blogWithAction === "dialog" ||
		postData.dialog === "full" ||
		postData.dialog === "only";

	if (isLinkAction) {
		if (!postData.blogActionUrl) {
			return null;
		}

		return (
			<a href={postData.blogActionUrl} className="pd-btn pd-btn--primary aside-sticky__button">
				{actionText}
			</a>
		);
	}

	if (isDialogAction) {
		return (
			<button
				type="button"
				className="pd-btn pd-btn--primary aside-sticky__button"
				data-a11y-dialog-show={getDialogIdByTaxType(postData.blogPodatek)}
			>
				{actionText}
			</button>
		);
	}

	if (postData.blogActionUrl) {
		return (
			<a href={postData.blogActionUrl} className="pd-btn pd-btn--primary aside-sticky__button">
				{actionText}
			</a>
		);
	}

	return null;
};

export async function getServerSideProps({ params, res }) {
	res.setHeader("Cache-Control", "no-store, max-age=0");

	const postData = await getPostData(params.id);

	if (!postData) {
		return {
			notFound: true,
		};
	}

	const allPostsData = getSortedPostsData();

	const mdxSource = await serialize(postData.contentHtml, {
		mdxOptions: {
			remarkPlugins: [],
			rehypePlugins: [],
		},
	});

	const relatedPosts = allPostsData.filter((item) => item.id !== params.id).slice(0, 3);

	return {
		props: {
			postData,
			relatedPosts,
			source: mdxSource,
		},
	};
}

export default function Post({ postData, relatedPosts, source }) {
	const blogMiasto = "PRZYJAZNEDEKLARACJE";
	const blogPodatek = postData.blogPodatek;
	const resolvedAuthor = resolveArticleAuthor(postData);

	useEffect(() => {
		if (!postData.video) {
			return;
		}

		const TOP_OFFSET = 120;
		const BOTTOM_OFFSET = 640;
		const DESKTOP_BREAKPOINT = 992;

		const articleContainer = document.querySelector(".pd-article__container");

		if (!articleContainer) {
			return;
		}

		const sidebarWrapper = articleContainer.querySelector(".pd-entries__popular.absolute");
		const sidebarColumn = sidebarWrapper?.querySelector(".pd-entries__max-width");
		const stickyBox = sidebarColumn?.querySelector(".aside-sticky");

		if (!sidebarWrapper || !sidebarColumn || !stickyBox) {
			return;
		}

		const scrollToTableOfContents = () => {
			const target =
				document.getElementById("spis-tresci") ||
				document.getElementById("spis-treści") ||
				document.querySelector(".spis-tresci") ||
				document.querySelector(".table-of-contents") ||
				document.querySelector(".toc");

			if (target) {
				const targetTop = target.getBoundingClientRect().top + window.scrollY - TOP_OFFSET;

				window.scrollTo({
					top: Math.max(0, targetTop),
					behavior: "smooth",
				});

				return;
			}

			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		};

		const handleStickyClick = (event) => {
			const tocButton = event.target.closest("[data-scroll-to-toc='true']");

			if (!tocButton) {
				return;
			}

			event.preventDefault();
			scrollToTableOfContents();
		};

		stickyBox.addEventListener("click", handleStickyClick);

		const previousPlaceholder = sidebarColumn.querySelector(".aside-sticky-placeholder");

		if (previousPlaceholder) {
			previousPlaceholder.remove();
		}

		const placeholder = document.createElement("div");
		placeholder.className = "aside-sticky-placeholder";
		placeholder.setAttribute("aria-hidden", "true");

		Object.assign(placeholder.style, {
			display: "block",
			width: "100%",
			height: "0px",
			margin: "0",
			padding: "0",
			border: "0",
			visibility: "hidden",
			pointerEvents: "none",
		});

		stickyBox.parentNode.insertBefore(placeholder, stickyBox);

		let frameId = 0;
		let resizeObserver = null;
		let isDestroyed = false;
		let currentState = "";

		const getScrollY = () => window.scrollY || window.pageYOffset || 0;

		const getPageTop = (element) => {
			const rect = element.getBoundingClientRect();
			return getScrollY() + rect.top;
		};

		const cancelScheduledUpdate = () => {
			if (frameId) {
				window.cancelAnimationFrame(frameId);
				frameId = 0;
			}
		};

		const moveStickyBackToColumn = () => {
			if (stickyBox.parentNode !== sidebarColumn) {
				sidebarColumn.insertBefore(stickyBox, placeholder.nextSibling);
			}
		};

		const moveStickyToBody = () => {
			if (stickyBox.parentNode !== document.body) {
				document.body.appendChild(stickyBox);
			}
		};

		const clearStickyStyles = () => {
			stickyBox.style.position = "";
			stickyBox.style.top = "";
			stickyBox.style.left = "";
			stickyBox.style.right = "";
			stickyBox.style.bottom = "";
			stickyBox.style.width = "";
			stickyBox.style.zIndex = "";
			stickyBox.style.transform = "";
			stickyBox.style.willChange = "";
			stickyBox.style.backfaceVisibility = "";
			stickyBox.style.webkitBackfaceVisibility = "";
		};

		const resetSticky = () => {
			currentState = "";

			moveStickyBackToColumn();

			placeholder.style.height = "0px";
			sidebarColumn.style.position = "";

			clearStickyStyles();
		};

		const setNormalState = () => {
			if (currentState === "normal" && stickyBox.parentNode === sidebarColumn) {
				return;
			}

			currentState = "normal";

			moveStickyBackToColumn();

			placeholder.style.height = "0px";

			clearStickyStyles();
		};

		const setFixedState = ({ left, width, height }) => {
			currentState = "fixed";

			placeholder.style.height = `${height}px`;

			moveStickyToBody();

			stickyBox.style.position = "fixed";
			stickyBox.style.top = `${TOP_OFFSET}px`;
			stickyBox.style.left = `${left}px`;
			stickyBox.style.right = "auto";
			stickyBox.style.bottom = "auto";
			stickyBox.style.width = `${width}px`;
			stickyBox.style.zIndex = "0";
			stickyBox.style.transform = "none";
			stickyBox.style.willChange = "auto";
			stickyBox.style.backfaceVisibility = "hidden";
			stickyBox.style.webkitBackfaceVisibility = "hidden";
		};

		const setBottomState = ({ top, height }) => {
			currentState = "bottom";

			moveStickyBackToColumn();

			placeholder.style.height = `${height}px`;

			stickyBox.style.position = "absolute";
			stickyBox.style.top = `${top}px`;
			stickyBox.style.left = "0";
			stickyBox.style.right = "auto";
			stickyBox.style.bottom = "auto";
			stickyBox.style.width = "100%";
			stickyBox.style.zIndex = "20";
			stickyBox.style.transform = "none";
			stickyBox.style.willChange = "auto";
			stickyBox.style.backfaceVisibility = "hidden";
			stickyBox.style.webkitBackfaceVisibility = "hidden";
		};

		const updateSticky = () => {
			frameId = 0;

			if (isDestroyed) {
				return;
			}

			if (window.innerWidth <= DESKTOP_BREAKPOINT) {
				resetSticky();
				return;
			}

			sidebarColumn.style.position = "relative";

			const scrollY = getScrollY();

			if (currentState === "fixed") {
				moveStickyBackToColumn();
			}

			clearStickyStyles();

			const articleTop = getPageTop(articleContainer);
			const articleHeight = articleContainer.offsetHeight;
			const articleBottom = articleTop + articleHeight;

			const columnTop = getPageTop(sidebarColumn);
			const columnRect = sidebarColumn.getBoundingClientRect();

			const stickyStart = getPageTop(placeholder);
			const stickyHeight = stickyBox.offsetHeight;

			const fixedStartScroll = stickyStart - TOP_OFFSET;
			const bottomStartScroll = articleBottom - stickyHeight - BOTTOM_OFFSET - TOP_OFFSET;

			const columnLeft = columnRect.left;
			const columnWidth = columnRect.width;

			if (scrollY < fixedStartScroll) {
				setNormalState();
				return;
			}

			if (scrollY >= bottomStartScroll) {
				const top = Math.max(0, articleBottom - stickyHeight - BOTTOM_OFFSET - columnTop);

				setBottomState({
					top,
					height: stickyHeight,
				});

				return;
			}

			setFixedState({
				left: columnLeft,
				width: columnWidth,
				height: stickyHeight,
			});
		};

		const scheduleUpdate = () => {
			if (isDestroyed || frameId) {
				return;
			}

			frameId = window.requestAnimationFrame(updateSticky);
		};

		const forceUpdate = () => {
			cancelScheduledUpdate();
			updateSticky();
		};

		const handleResize = () => {
			resetSticky();
			scheduleUpdate();
		};

		forceUpdate();

		window.addEventListener("scroll", scheduleUpdate, { passive: true });
		window.addEventListener("resize", handleResize);
		window.addEventListener("load", scheduleUpdate);

		if (typeof window.ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(() => {
				scheduleUpdate();
			});

			resizeObserver.observe(articleContainer);
			resizeObserver.observe(sidebarColumn);
			resizeObserver.observe(stickyBox);
		}

		if (document.fonts?.ready) {
			document.fonts.ready.then(() => {
				scheduleUpdate();
			});
		}

		return () => {
			isDestroyed = true;

			cancelScheduledUpdate();

			stickyBox.removeEventListener("click", handleStickyClick);

			window.removeEventListener("scroll", scheduleUpdate);
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("load", scheduleUpdate);

			if (resizeObserver) {
				resizeObserver.disconnect();
			}

			resetSticky();

			if (placeholder.parentNode) {
				placeholder.parentNode.removeChild(placeholder);
			}
		};
	}, [postData.video]);

	useEffect(() => {
		const faq = document.querySelector(".video-faq");

		if (!faq) {
			return;
		}

		const handleFaqClick = (event) => {
			const button = event.target.closest(".video-faq__toggle");

			if (!button || !faq.contains(button)) {
				return;
			}

			const answerId = button.getAttribute("aria-controls");
			const answer = document.getElementById(answerId);
			const item = button.closest(".video-faq__item");

			if (!answer) {
				return;
			}

			const isExpanded = button.getAttribute("aria-expanded") === "true";
			const nextExpanded = !isExpanded;

			answer.hidden = !nextExpanded;
			button.setAttribute("aria-expanded", String(nextExpanded));
			button.textContent = nextExpanded ? "Ukryj odpowiedź" : "Pokaż odpowiedź";

			if (item) {
				item.classList.toggle("is-collapsed", !nextExpanded);
			}
		};

		faq.addEventListener("click", handleFaqClick);

		return () => {
			faq.removeEventListener("click", handleFaqClick);
		};
	}, [source]);

	return (
		<Layout
			article
			title={postData.title}
			description={postData.description}
			seoTitle={postData.seoTitle}
			seoDescription={postData.seoDescription}
			seoImage={postData.seoImage}
			imageDesktop={postData.imageDesktop}
			imageTablet={postData.imageTablet}
			imageMobile={postData.imageMobile}
			datePublication={postData.datePublication}
			dateUpdate={postData.dateUpdate}
			hours={postData.hours}
			author={resolvedAuthor.name}
			authorId={resolvedAuthor.id}
			authorVariable={resolvedAuthor.legacyVariable}
			blogMiasto={postData.blogMiasto}
			blogPodatek={postData.blogPodatek}
			blogWithAction={postData.blogWithAction}
			blogActionText={postData.blogActionText}
			blogActionUrl={postData.blogActionUrl}
		>
			<ArticleStructuredData postData={postData} section="abc-podatkow" />

			<section className="pd-article__container">
				<article className="pd-article">
					<ArticleMetaProvider value={postData}>
						<MDXRemote {...source} components={components} />
					</ArticleMetaProvider>

					<div className="pd-article__dialog">
						<div className="pd-article__dialog__wrapper">
							{postData.dialog === "only" && <DeclarationModal miasto={postData.blogMiasto} />}

							{postData.dialog === "full" && (
								<Section id="wypelnij-deklaracje" title="sections.wypelnijdeklaracje.title">
									<DeclarationBox center>
										<DeclarationBoxItem
											miasto={postData.blogMiasto}
											podatek={blogPodatek}
											noReadMore
										/>
									</DeclarationBox>

									<DeclarationModal miasto={postData.blogMiasto} podatek={blogPodatek} />
								</Section>
							)}
						</div>
					</div>

					<aside className="pd-entries__popular absolute">
						<div className="pd-entries__max-width">
							{resolvedAuthor.id !== "redakcja" && (
								<>
									<h2 className="pd-entries__title">Autor artykułu:</h2>
									<Author author={resolvedAuthor.id} />
								</>
							)}

							{!postData.video ? (
								<>
									<h2 className="pd-entries__title">Podobne wpisy:</h2>

									{relatedPosts.map(({ id, datePublication, title, author }, index) => (
										<article key={index} className="pd-entry">
											<header className="pd-entry__header">
												<h3 className="pd-entry__title">
													<Link
														href={`/abc-podatkow/${id}/`}
														aria-label={"Czytaj więcej o " + title.toLowerCase()}
													>
														{title}
													</Link>
												</h3>
											</header>

											<p className="pd-entry__meta">
												<span className="pd-entry__meta__date">
													<time dateTime={dayjs(datePublication).format("YYYY-MM-DD")}>
														{dayjs(datePublication).format("D MMMM YYYY")}
													</time>
												</span>

												<span className="pd-entry__meta__author">{author}</span>
											</p>

											<footer className="pd-entry__footer">
												<Link
													href={`/abc-podatkow/${id}/`}
													aria-label={"Czytaj więcej o " + title.toLowerCase()}
													className="pd-entry__readmore"
												>
													Czytaj więcej
												</Link>
											</footer>
										</article>
									))}
								</>
							) : (
								<aside className="aside-sticky">
									<div className="aside-title">Skorzystaj z przyjaznego kreatora</div>

									<ul>
										<li>
											<CheckIcon />
											poprowadzi Cię krok po kroku
										</li>

										<li>
											<CheckIcon />
											automatycznie dobierze prawidłowe stawki
										</li>

										<li>
											<CheckIcon />
											automatycznie policzy podatek
										</li>

										<li>
											<CheckIcon />
											wygeneruje komplet dokumentów
										</li>

										<li>
											<CheckIcon />
											zawiera poradniki <span className="asideVideo">Video</span>
										</li>
									</ul>

									<AsideStickyAction postData={postData} />

									<button
										type="button"
										className="aside-sticky__toc-button"
										data-scroll-to-toc="true"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
										>
											<path
												fill-rule="evenodd"
												clip-rule="evenodd"
												d="M3.29289 9.70711C2.90237 9.31658 2.90237 8.68342 3.29289 8.29289L9.29289 2.29289C9.68342 1.90237 10.3166 1.90237 10.7071 2.29289L16.7071 8.29289C17.0976 8.68342 17.0976 9.31658 16.7071 9.70711C16.3166 10.0976 15.6834 10.0976 15.2929 9.70711L11 5.41421L11 17C11 17.5523 10.5523 18 10 18C9.44772 18 9 17.5523 9 17L9 5.41421L4.70711 9.70711C4.31658 10.0976 3.68342 10.0976 3.29289 9.70711Z"
												fill="#0377BA"
											/>
										</svg>
										Wróć do spisu treści
									</button>
								</aside>
							)}
						</div>
					</aside>
				</article>
			</section>

			<Section>
				<BackButton />
			</Section>

			<style jsx global>{`
				html {
					scroll-padding-top: 60px;
				}

				.pd-article [id],
				.pd-article h2[id],
				.pd-article h3[id],
				.pd-article section[id] {
					scroll-margin-top: 60px;
				}

				@media (max-width: 991px) {
					html {
						scroll-padding-top: 40px;
					}

					.pd-article [id],
					.pd-article h2[id],
					.pd-article h3[id],
					.pd-article section[id] {
						scroll-margin-top: 40px;
					}
				}
			`}</style>

			<style jsx global>{`
				.wzory.article {
					grid-template-columns: 1fr;
					gap: 32px;
				}

				.wzory.article p {
					margin: 0 !important;
				}

				.wzory.article a.wzor-download {
					margin-top: 16px;
				}

				.wzory.article .wzor-content {
					padding-left: 16px;
				}

				.wzor-image {
					flex: 0 0 64px;
				}
				,
				.wzor-icon {
					width/height: 72px;
				}

				.aside-sticky {
					position: relative;
					font-size: 1rem;
					line-height: 1.882rem;
					padding: 24px 24px 48px;
					width: 100%;
					background-color: #fff;
					box-shadow: 0 20px 40px 0 rgba(0, 0, 0, 0.05);
					border-radius: 10px;
					box-sizing: border-box;
				}

				.aside-sticky-placeholder {
					display: block;
					width: 100%;
					height: 0;
					margin: 0;
					padding: 0;
					border: 0;
					visibility: hidden;
					pointer-events: none;
				}

				.aside-title {
					font-size: 20px;
					font-weight: 500;
				}

				.aside-sticky ul {
					list-style: none !important;
					padding-left: 8px;
					margin-left: 0;
				}

				.aside-sticky ul li {
					padding-bottom: 16px;
					margin-bottom: 0;
				}

				.aside-sticky ul li::before {
					display: none;
				}

				.aside-sticky ul li svg {
					margin-right: 8px;
				}

				.aside-sticky .pd-btn,
				.aside-sticky__button {
					width: 100%;
					padding: 12px;
				}

				.aside-sticky__button {
					display: inline-flex;
					align-items: center;
					justify-content: center;
					text-align: center;
					text-decoration: none;
				}

				.aside-sticky__toc-button {
					position: absolute;
					bottom: -64px;
					left: 0;
					border: 0;
					background: transparent;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					gap: 8px;
					width: 100%;
					margin-top: 16px;
					padding: 12px;
					border-radius: 8px;
					color: #0377ba;
					font: inherit;
					font-weight: 500;
					line-height: 1.2;
					text-align: center;
					cursor: pointer;
				}

				.aside-sticky__toc-button:hover {
					background: transparent;
					text-decoration: underline;
				}

				span.asideVideo {
					padding: 4px 8px;
					background: #000;
					color: #fff;
					border-radius: 10px;
				}
			`}</style>

			<style jsx global>{`
				asidetext[border],
				.border-text {
					color: #222;
					border: 1px solid #0377ba;
				}

				.author__card {
					width: 100%;
					padding: 32px 0 64px;
				}

				.author {
					display: flex;
					align-items: center;
					gap: 20px;
				}

				.author__avatar {
					width: 48px;
					height: 48px;
					border-radius: 50%;
					object-fit: cover;
					flex-shrink: 0;
					background: #eee;
				}

				.author__title {
					margin: 0 0 6px;
					font-size: 16px;
					line-height: 1.15;
					font-weight: 500;
					color: #222;
					letter-spacing: 0.2px;
				}

				.author__role {
					margin: 0;
					font-size: 12px;
					line-height: 1.2;
					font-weight: 400;
					color: #555;
				}

				.author__card__text {
					font-size: 16px;
					line-height: 1.38;
					font-weight: 400;
					color: #414141;
					letter-spacing: 0.2px;
				}

				.author__card__link {
					color: var(--video-color-link);
					font-size: var(--video-font-size-base);
					line-height: 1.25;
					text-decoration: none;
					margin-top: auto;
					align-self: flex-start;
				}

				.author__card__link:hover {
					text-decoration: underline;
				}

				.related-video__grid {
					gap: 1rem;
				}

				ul.pd-custom-list,
				.pd-article ul {
					margin-top: 0;
				}

				span.link-to.block {
					padding-bottom: 0;
				}

				h2 {
					font-size: 20px;
					font-weight: 500;
				}

				h2 {
					font-size: 20px !important;
				}

				h3 {
					font-weight: 500;
				}

				h4 {
					font-weight: 500;
				}

				.pd-panel .aside-text.border-text {
					padding: 32px;
				}

				.wzory {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 16px;
				}

				.wzor {
					display: flex;
					gap: 16px;
					box-shadow: 0 20px 40px 0 rgba(0, 0, 0, 0.05);
					padding: 16px;
					border-radius: 10px;
				}

				a.wzor-download {
					display: flex;
					align-items: center;
					gap: 8px;
				}

				a.wzor-download p {
					padding: 0;
					margin: 0;
					font-weight: 500;
				}

				.wzor-content {
					display: flex;
					flex-direction: column;
					justify-content: space-between;
				}

				.wzor-title {
					line-height: 1.6;
				}

				section.pd-panel {
					padding: 32px;
				}

				.video-faq__toggle {
					background: transparent;
					border: 0;
					padding: 0;
					color: #0377ba;
				}

				.video-faq__toggle:hover {
					text-decoration: underline;
					cursor: pointer;
				}

				.video-faq__toggle p {
					color: #0377ba;
				}

				@media (max-width: 600px) {
					section.pd-panel {
						padding: 16px;
					}

					.wzory {
						grid-template-columns: 1fr;
					}

					.pd-hero__header__date {
						flex-direction: column;
						align-items: flex-start;
						gap: 0;
					}
				}
			`}</style>
		</Layout>
	);
}
